import { ByteLruCache } from "../lib/ByteLruCache";
import {
  normalizeTownAvatarAppearance,
  townAvatarSpriteAppearance,
  townAvatarAppearanceKey,
  type LegacyTownAvatarAppearance,
  LEGACY_TOWN_AVATAR_CLOTHING_HEX,
  LEGACY_TOWN_AVATAR_HAIR_HEX,
  LEGACY_TOWN_AVATAR_SKIN_HEX,
  type TownAvatarAppearance,
  type TownFacing,
} from "@town/contracts";
import { PIXEL } from "./palette";
import { createPixelCanvas, paintSprite, Painter, shade, type Sprite } from "./paint";

export const CHARACTER_SPRITE_WIDTH = 24;
export const CHARACTER_SPRITE_HEIGHT = 36;
export const CHARACTER_WALK_FRAMES = 4;
/** Seated sprites tuck the legs, so the body sits four pixels lower. */
export const CHARACTER_SEATED_DROP = 4;

export type CharacterFrame = 0 | 1 | 2 | 3;

export type CharacterSheet = Readonly<{
  walk: Readonly<Record<TownFacing, readonly Sprite[]>>;
  seated: Readonly<Record<TownFacing, Sprite>>;
}>;

type FigureColors = Readonly<{
  skin: string;
  skinShade: string;
  hair: string;
  hairShade: string;
  hairLight: string;
  top: string;
  topShade: string;
  bottom: string;
  bottomShade: string;
  shoe: string;
}>;

const SHOE = "#3b3340";
const EYE = "#2b2536";
const BLUSH = "#e89a90";

function figureColors(appearance: LegacyTownAvatarAppearance): FigureColors {
  const skin = LEGACY_TOWN_AVATAR_SKIN_HEX[appearance.skinTone];
  const hair = LEGACY_TOWN_AVATAR_HAIR_HEX[appearance.hairColor];
  const top = LEGACY_TOWN_AVATAR_CLOTHING_HEX[appearance.topColor];
  const bottom = LEGACY_TOWN_AVATAR_CLOTHING_HEX[appearance.bottomColor];
  return {
    skin,
    skinShade: shade(skin, -0.18),
    hair,
    hairShade: shade(hair, -0.28),
    hairLight: shade(hair, 0.22),
    top,
    topShade: shade(top, -0.22),
    bottom,
    bottomShade: shade(bottom, -0.22),
    shoe: SHOE,
  };
}

function torsoWidth(bodyStyle: LegacyTownAvatarAppearance["bodyStyle"]): number {
  return bodyStyle === "masculine" ? 12 : bodyStyle === "feminine" ? 8 : 10;
}

type Pose = Readonly<{
  facing: TownFacing;
  frame: CharacterFrame;
  seated: boolean;
}>;

function drawFrontHair(p: Painter, c: FigureColors, style: LegacyTownAvatarAppearance["hairStyle"], bob: number, back: boolean) {
  const y = 3 + bob;
  switch (style) {
    case "buzz":
      p.rect(6, y, 12, 3, c.hair);
      p.rect(5, y + 1, 1, 2, c.hair);
      p.rect(18, y + 1, 1, 2, c.hair);
      if (back) p.rect(6, y + 3, 12, 3, c.hair);
      break;
    case "curly":
      p.rect(6, y - 1, 12, 1, c.hair);
      p.rect(5, y, 14, 6, c.hair);
      p.rect(4, y + 2, 1, 5, c.hair);
      p.rect(19, y + 2, 1, 5, c.hair);
      p.rect(5, y + 6, 2, 2, c.hair);
      p.rect(17, y + 6, 2, 2, c.hair);
      if (back) p.rect(5, y + 5, 14, 5, c.hair);
      break;
    case "bob":
      p.rect(6, y, 12, 5, c.hair);
      p.rect(5, y + 1, 1, 3, c.hair);
      p.rect(18, y + 1, 1, 3, c.hair);
      p.rect(5, y + 3, 2, 8, c.hair);
      p.rect(17, y + 3, 2, 8, c.hair);
      p.hline(6, y + 5, 3, c.hair);
      p.hline(15, y + 5, 3, c.hair);
      if (back) p.rect(6, y + 5, 12, 6, c.hair);
      break;
    case "long":
      p.rect(6, y, 12, 5, c.hair);
      p.rect(5, y + 1, 1, 3, c.hair);
      p.rect(18, y + 1, 1, 3, c.hair);
      p.rect(5, y + 3, 2, 16, c.hair);
      p.rect(17, y + 3, 2, 16, c.hair);
      p.rect(4, y + 11, 1, 8, c.hairShade);
      p.rect(19, y + 11, 1, 8, c.hairShade);
      p.hline(6, y + 5, 3, c.hair);
      p.hline(15, y + 5, 3, c.hair);
      if (back) p.rect(6, y + 5, 12, 15, c.hair);
      break;
    case "ponytail":
      p.rect(6, y, 12, 5, c.hair);
      p.rect(5, y + 1, 1, 3, c.hair);
      p.rect(18, y + 1, 1, 3, c.hair);
      p.hline(6, y + 5, 3, c.hair);
      p.hline(15, y + 5, 3, c.hair);
      if (back) {
        p.rect(6, y + 5, 12, 4, c.hair);
        p.rect(11, y + 8, 2, 11, c.hairShade);
        p.rect(10, y + 7, 4, 2, c.hair);
      }
      break;
    default:
      p.rect(6, y, 12, 5, c.hair);
      p.rect(5, y + 1, 1, 3, c.hair);
      p.rect(18, y + 1, 1, 3, c.hair);
      p.hline(6, y + 5, 3, c.hair);
      p.hline(15, y + 5, 3, c.hair);
      if (back) p.rect(6, y + 5, 12, 4, c.hair);
  }
  // A rounded dome: the crown steps in two pixels per row like the 3D kit's hair (buzz stays snug).
  if (style !== "buzz" && style !== "curly") p.rect(8, y - 1, 8, 1, c.hair);
  // One short highlight band on the upper front of the crown, off to the character's right.
  if (!back) p.hline(7, y + 1, 4, c.hairLight);
}

function drawProfileHair(p: Painter, c: FigureColors, style: LegacyTownAvatarAppearance["hairStyle"], bob: number) {
  const y = 3 + bob;
  // Like the 3D kit, the side of the head is hair from the nape to the jaw; only the face's edge shows.
  p.rect(6, y + 1, 10, 10, c.hair);
  switch (style) {
    case "buzz":
      p.rect(7, y, 10, 3, c.hair);
      break;
    case "curly":
      p.rect(6, y - 1, 12, 7, c.hair);
      p.rect(5, y + 2, 1, 8, c.hair);
      break;
    case "bob":
      p.rect(7, y, 10, 5, c.hair);
      p.rect(5, y + 2, 3, 10, c.hair);
      break;
    case "long":
      p.rect(7, y, 10, 5, c.hair);
      p.rect(4, y + 2, 4, 17, c.hair);
      p.rect(4, y + 12, 2, 7, c.hairShade);
      break;
    case "ponytail":
      p.rect(7, y, 10, 5, c.hair);
      p.rect(4, y + 5, 2, 11, c.hairShade);
      p.rect(3, y + 9, 2, 5, c.hairShade);
      break;
    default:
      p.rect(7, y, 10, 5, c.hair);
  }
  if (style !== "buzz" && style !== "curly") p.rect(8, y - 1, 8, 1, c.hair);
  p.hline(12, y + 1, 3, c.hairLight);
}

function drawFrontLegs(p: Painter, c: FigureColors, a: LegacyTownAvatarAppearance, pose: Pose, torsoX: number, width: number) {
  const skirted = a.outfitStyle === "dress" || a.outfitStyle === "shirt-skirt";
  if (pose.seated) {
    const thighColor = skirted ? (a.outfitStyle === "dress" ? c.top : c.bottom) : c.bottom;
    p.rect(7, 27, 10, 3, thighColor);
    p.hline(7, 29, 10, skirted ? shade(thighColor, -0.2) : c.bottomShade);
    p.rect(8, 30, 3, 3, skirted ? c.skin : c.bottom);
    p.rect(13, 30, 3, 3, skirted ? c.skin : c.bottom);
    p.rect(7, 33, 4, 2, c.shoe);
    p.rect(13, 33, 4, 2, c.shoe);
    return;
  }
  const leftLift = pose.frame === 1 ? 1 : 0;
  const rightLift = pose.frame === 3 ? 1 : 0;
  if (skirted) {
    const skirt = a.outfitStyle === "dress" ? c.top : c.bottom;
    const skirtShade = a.outfitStyle === "dress" ? c.topShade : c.bottomShade;
    const skirtTop = a.outfitStyle === "dress" ? 23 : 25;
    p.rect(torsoX - 1, skirtTop, width + 2, 3, skirt);
    p.rect(torsoX - 2, skirtTop + 3, width + 4, 30 - skirtTop - 3, skirt);
    p.hline(torsoX - 2, 29, width + 4, skirtShade);
    p.rect(9, 30 - leftLift, 2, 4, c.skin);
    p.rect(13, 30 - rightLift, 2, 4, c.skin);
    p.rect(8, 34 - leftLift, 4, 2, c.shoe);
    p.rect(12, 34 - rightLift, 4, 2, c.shoe);
    return;
  }
  p.rect(8, 27, 8, 2, c.bottom);
  p.rect(8, 27, 3, 7 - leftLift, c.bottom);
  p.rect(13, 27, 3, 7 - rightLift, c.bottom);
  p.vline(10, 28, 6 - leftLift, c.bottomShade);
  p.vline(15, 28, 6 - rightLift, c.bottomShade);
  p.rect(7, 34 - leftLift, 4, 2, c.shoe);
  p.rect(13, 34 - rightLift, 4, 2, c.shoe);
}

function drawFrontTorso(p: Painter, c: FigureColors, a: LegacyTownAvatarAppearance, pose: Pose, bob: number) {
  const width = torsoWidth(a.bodyStyle);
  const x = 12 - width / 2;
  const torsoBottom = a.outfitStyle === "dress" ? 23 : a.outfitStyle === "shirt-skirt" ? 25 : 27;
  p.rect(x, 17 + bob, width, torsoBottom - 17, c.top);
  p.vline(x + width - 1, 18 + bob, torsoBottom - 18, c.topShade);
  if (a.outfitStyle === "overalls") {
    p.rect(10, 19 + bob, 4, torsoBottom - 19, c.bottom);
    p.vline(10, 17 + bob, 2, c.bottom);
    p.vline(13, 17 + bob, 2, c.bottom);
    p.px(11, 21 + bob, c.bottomShade);
    p.px(12, 21 + bob, c.bottomShade);
  }
  const leftSwing = pose.seated ? 0 : pose.frame === 1 ? -1 : pose.frame === 3 ? 1 : 0;
  const rightSwing = -leftSwing;
  const armLeftX = x - 2;
  const armRightX = x + width;
  p.rect(armLeftX, 18 + bob + leftSwing, 2, 4, c.top);
  p.rect(armLeftX, 22 + bob + leftSwing, 2, 3, c.skin);
  p.rect(armRightX, 18 + bob + rightSwing, 2, 4, c.topShade);
  p.rect(armRightX, 22 + bob + rightSwing, 2, 3, c.skin);
  drawFrontLegs(p, c, a, pose, x, width);
}

function drawFrontFigure(p: Painter, c: FigureColors, a: LegacyTownAvatarAppearance, pose: Pose) {
  const bob = !pose.seated && (pose.frame === 1 || pose.frame === 3) ? -1 : 0;
  const back = pose.facing === "north";
  drawFrontTorso(p, c, a, pose, bob);
  p.rect(10, 16 + bob, 4, 1, c.skinShade);
  p.rect(6, 4 + bob, 12, 12, c.skin);
  p.hline(6, 15 + bob, 12, c.skinShade);
  p.vline(17, 5 + bob, 10, c.skinShade);
  if (!back) {
    p.rect(9, 10 + bob, 1, 2, EYE);
    p.rect(14, 10 + bob, 1, 2, EYE);
    p.px(8, 12 + bob, BLUSH);
    p.px(15, 12 + bob, BLUSH);
  }
  drawFrontHair(p, c, a.hairStyle, bob, back);
}

function drawProfileFigure(p: Painter, c: FigureColors, a: LegacyTownAvatarAppearance, pose: Pose) {
  const bob = !pose.seated && (pose.frame === 1 || pose.frame === 3) ? -1 : 0;
  const skirted = a.outfitStyle === "dress" || a.outfitStyle === "shirt-skirt";
  const torsoBottom = a.outfitStyle === "dress" ? 23 : a.outfitStyle === "shirt-skirt" ? 25 : 27;
  const width = a.bodyStyle === "masculine" ? 9 : 8;
  const x = 8;

  if (pose.seated) {
    const thighColor = skirted ? (a.outfitStyle === "dress" ? c.top : c.bottom) : c.bottom;
    p.rect(9, 27, 8, 3, thighColor);
    p.rect(13, 30, 3, 3, skirted ? c.skin : c.bottom);
    p.rect(13, 33, 4, 2, c.shoe);
  } else {
    const stride = pose.frame === 1 ? 2 : pose.frame === 3 ? -2 : 0;
    if (skirted) {
      const skirt = a.outfitStyle === "dress" ? c.top : c.bottom;
      p.rect(x - 1, torsoBottom, width + 2, 3, skirt);
      p.rect(x - 2, torsoBottom + 3, width + 4, 30 - torsoBottom - 3, skirt);
      p.rect(10 - stride / 2, 30, 2, 4, c.skinShade);
      p.rect(12 + stride / 2, 30, 2, 4, c.skin);
      p.rect(9 - stride / 2, 34, 4, 2, c.shoe);
      p.rect(12 + stride / 2, 34, 4, 2, c.shoe);
    } else {
      p.rect(9, 27, 6, 2, c.bottom);
      p.rect(9 - stride / 2, 27, 3, 7, c.bottomShade);
      p.rect(12 + stride / 2, 27, 3, 7, c.bottom);
      p.rect(8 - stride / 2, 34, 4, 2, c.shoe);
      p.rect(12 + stride / 2, 34, 4, 2, c.shoe);
    }
  }

  p.rect(x, 17 + bob, width, torsoBottom - 17, c.top);
  p.vline(x + width - 1, 18 + bob, torsoBottom - 18, c.topShade);
  if (a.outfitStyle === "overalls") {
    p.rect(11, 19 + bob, 3, torsoBottom - 19, c.bottom);
    p.vline(12, 17 + bob, 2, c.bottom);
  }
  const swing = pose.seated ? 2 : pose.frame === 1 ? 1 : pose.frame === 3 ? -1 : 0;
  p.rect(11 + swing, 18 + bob, 3, 4, c.topShade);
  p.rect(11 + swing, 22 + bob, 3, 3, c.skin);

  p.rect(10, 16 + bob, 3, 1, c.skinShade);
  p.rect(7, 4 + bob, 10, 12, c.skin);
  p.hline(7, 15 + bob, 10, c.skinShade);
  drawProfileHair(p, c, a.hairStyle, bob);
}

function outlined(base: Sprite, anchorY: number): Sprite {
  return paintSprite(
    base.width,
    base.height,
    (p) => {
      p.blit(base, -1, 0);
      p.blit(base, 1, 0);
      p.blit(base, 0, -1);
      p.blit(base, 0, 1);
    },
    { x: base.anchorX, y: anchorY },
  );
}

function renderPose(appearance: LegacyTownAvatarAppearance, colors: FigureColors, pose: Pose): Sprite {
  const anchorY = pose.seated
    ? CHARACTER_SPRITE_HEIGHT - 1
    : CHARACTER_SPRITE_HEIGHT;
  const body = paintSprite(CHARACTER_SPRITE_WIDTH, CHARACTER_SPRITE_HEIGHT, (p) => {
    if (pose.facing === "east" || pose.facing === "west") {
      drawProfileFigure(p, colors, appearance, pose);
    } else {
      drawFrontFigure(p, colors, appearance, pose);
    }
  });
  const silhouette = outlined(body, anchorY);
  const context = silhouette.canvas.getContext("2d");
  if (!context) throw new Error("2D canvas is unavailable");
  context.globalCompositeOperation = "source-in";
  context.fillStyle = PIXEL.ink;
  context.fillRect(0, 0, silhouette.width, silhouette.height);
  context.globalCompositeOperation = "source-over";
  context.drawImage(body.canvas, 0, 0);
  if (pose.facing === "west") {
    const mirrored = createPixelCanvas(silhouette.width, silhouette.height);
    const mirrorContext = mirrored.getContext("2d");
    if (!mirrorContext) throw new Error("2D canvas is unavailable");
    mirrorContext.imageSmoothingEnabled = false;
    mirrorContext.translate(silhouette.width, 0);
    mirrorContext.scale(-1, 1);
    mirrorContext.drawImage(silhouette.canvas, 0, 0);
    return { ...silhouette, canvas: mirrored };
  }
  return silhouette;
}

const FACINGS: readonly TownFacing[] = ["south", "north", "east", "west"];

export function characterSheetKey(appearance: TownAvatarAppearance): string {
  return townAvatarAppearanceKey(appearance);
}

const sheetCache = new ByteLruCache<string, CharacterSheet>(16 * 1024 * 1024, sheet => [...Object.values(sheet.walk).flat(), ...Object.values(sheet.seated)].reduce((bytes, sprite) => bytes + sprite.canvas.width * sprite.canvas.height * 4, 0));

export function buildCharacterSheet(appearance: TownAvatarAppearance): CharacterSheet {
  const fallback = townAvatarSpriteAppearance(appearance);
  const colors = figureColors(fallback);
  const walk = {} as Record<TownFacing, Sprite[]>;
  const seated = {} as Record<TownFacing, Sprite>;
  for (const facing of FACINGS) {
    walk[facing] = ([0, 1, 2, 3] as const).map((frame) =>
      renderPose(fallback, colors, { facing, frame, seated: false }),
    );
    seated[facing] = renderPose(fallback, colors, { facing, frame: 0, seated: true });
  }
  return { walk, seated };
}

export function getCharacterSheet(
  appearance: TownAvatarAppearance | undefined,
  avatar: number,
): CharacterSheet {
  const normalized = normalizeTownAvatarAppearance(appearance, avatar);
  const key = characterSheetKey(normalized);
  const cached = sheetCache.get(key);
  if (cached) return cached;
  const sheet = buildCharacterSheet(normalized);
  sheetCache.set(key, sheet);
  return sheet;
}

/** Picks the walk frame for a moving character from elapsed seconds. */
export function walkFrameAt(seconds: number, running: boolean | "walk" | "run" | "sprint"): CharacterFrame {
  const cycle = running === "sprint" ? .24 : running === true || running === "run" ? .34 : .48;
  const step = Math.floor((seconds % cycle) / (cycle / CHARACTER_WALK_FRAMES));
  return Math.max(0, Math.min(3, step)) as CharacterFrame;
}

export type SpeakingFrame = 0 | 1 | 2;
const SPEECH_CYCLE: readonly SpeakingFrame[] = [0, 1, 2, 1, 0, 2, 0, 1];

export function speakingFrameAt(seconds: number): SpeakingFrame {
  if (!Number.isFinite(seconds) || seconds < 0) return 0;
  return SPEECH_CYCLE[Math.floor(seconds * 8) % SPEECH_CYCLE.length]!;
}

/** Animate only the head. The sprite's lower body and chair contact stay fixed. */
export function drawSpeakingCharacter(
  context: CanvasRenderingContext2D,
  sprite: Sprite,
  x: number,
  y: number,
  facing: TownFacing,
  walkingFrame: CharacterFrame,
  seated: boolean,
  speech: SpeakingFrame,
): void {
  if (speech === 0) {
    context.drawImage(sprite.canvas, x, y);
    return;
  }
  const headHeight = 17;
  const nod = speech === 2 ? 1 : 0;
  context.drawImage(sprite.canvas, 0, headHeight, sprite.width, sprite.height - headHeight,
    x, y + headHeight, sprite.width, sprite.height - headHeight);
  context.drawImage(sprite.canvas, 0, 0, sprite.width, headHeight,
    x, y + nod, sprite.width, headHeight);
  if (facing === "north") return;
  const bob = !seated && (walkingFrame === 1 || walkingFrame === 3) ? -1 : 0;
  const mouthX = facing === "south" ? 11 : facing === "east" ? 16 : 7;
  const mouthWidth = facing === "south" ? 2 : 1;
  const mouthY = y + 14 + bob + nod;
  context.fillStyle = EYE;
  context.fillRect(x + mouthX, mouthY, mouthWidth, speech);
  if (speech === 2) {
    context.fillStyle = BLUSH;
    context.fillRect(x + mouthX, mouthY + 1, mouthWidth, 1);
  }
}

/**
 * Draws a standing, south-facing preview into a DOM canvas at an integer
 * scale. Used by the avatar picker and the customizer.
 */
export function drawCharacterPreview(
  canvas: HTMLCanvasElement,
  appearance: TownAvatarAppearance | undefined,
  avatar: number,
  scale = 4,
  facing: TownFacing = "south",
): void {
  const sheet = getCharacterSheet(appearance, avatar);
  const sprite = sheet.walk[facing][0]!;
  canvas.width = sprite.width * scale;
  canvas.height = sprite.height * scale;
  const context = canvas.getContext("2d");
  if (!context) return;
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(sprite.canvas, 0, 0, canvas.width, canvas.height);
}
